/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, HStack, Text, Tooltip } from '@chakra-ui/react';
import {
  PrimaryButton,
  CQTreeTable,
} from '@laxmimanogna/code-quick-components';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MyChartContext } from '../../../../providers/MyChartProvider';
import AppColors from '../../../../constants/AppColors';
import { EyeAvatarIcon } from '../../../../constants/IconData';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../../constants/Routes';
import { replaceRoute } from '../../../../utils/common.utils';

import {
  CHART_SUBHEADER,
  ChartStatusBadge,
  BatchStatusIndicator,
  AttachmentCell,
  CompletedDateCell,
  getBatchStatus,
} from './table_config';
import { createTreeTableProps, paginationProps } from '../../../../utils/cq_tree_table_props';
import AuditSheetFileViewer from '../../../audit_sheet/components/AuditSheetFileViewer';
import NotesDrawer from '../../../audit_sheet/components/NotesDrawer';

const columnHelper = createColumnHelper();

const RebuttedCharts = ({ onOpenNotes }) => {
  const myChartContext = useContext(MyChartContext);
  const [ordering, setOrdering] = useState('');
  const { rebuttedCharts, isRebuttedChartLoading, getRebuttedCharts, filterParams } =
    myChartContext;
  const navigate = useNavigate();
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const tableRef = React.useRef(null);
  const PAGE_NAME = "My Chart";
  const TABLE_NAME = "Rebuttal Chart";

  useEffect(() => {
    if (myChartContext.rebuttedCharts.length > 0) {
      const itemId = getRebuttedCharts.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
      if (itemId) {
        setHighlightedRowId(itemId);
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
    }
  }, [myChartContext.rebuttedCharts]);

  useEffect(() => {
    let searching = !!(filterParams.searchText && filterParams.searchText.trim());
    getRebuttedCharts.loadAPI(PAGE_NAME, TABLE_NAME, searching);
  }, [filterParams, getRebuttedCharts.showOrdering]);

  useEffect(() => {
    getRebuttedCharts.setParams({ ordering });
  }, [ordering]);

  const columns = useMemo(() => [

    columnHelper.accessor('name', {
      id: 'name',
      header: () => 'Batch',
      subHeader: {
        1: { label: 'File', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const isBatch = info.row.depth === 0;
        const chartData = info.row.original;
        const cellValue = info.getValue();
        const isRebuttal = chartData.status === 'rebuttal' || chartData.status === 'completed';

        const isTruncated = cellValue?.length > 10;
        const truncatedValue = isTruncated
          ? `${cellValue.slice(0, 10)}...`
          : cellValue;

        if (isBatch) {
          return (
            <Tooltip
              hasArrow
              placement="bottom"
              label={isTruncated ? cellValue : ''}
            >
              <Text
                whiteSpace="nowrap"
                _hover={{ textDecoration: 'underline' }}
                cursor={'pointer'}
                onClick={() => {
                  const batchDashboard = replaceRoute(ROUTES.BATCH_DASHBOARD, {
                    batchId: chartData.upload_id,
                  });
                  navigate(batchDashboard, {
                    state: { chartData: chartData },
                  });
                }}
                fontWeight={600}
                color="blue.800"
                fontSize="14px"
                textAlign="start"
              >
                {truncatedValue}
              </Text>
            </Tooltip>
          );
        }

        return (
          <Text
            fontWeight={500}
            textAlign="start"
            color="blue.700"
            fontSize="13px"
          >
            {cellValue}
          </Text>
        );
      },
    }),

    columnHelper.accessor('upload_date', {
      id: 'upload_date',
      header: () => 'Upload Date',
      subHeader: {
        1: { label: 'Attachments', ...CHART_SUBHEADER },
      },
      cell: info => {
        const isBatch = info.row.depth === 0;
        const attachmentNames = [
          info.row.original.file_obj?.name,
          info.row.original.file_obj?.audited_chart_name,
          ...(info.row.original.attachments?.map(a => a.file.name) ?? [])
        ].filter(Boolean);
        if (isBatch) {
          const val = info.getValue();
          return val
            ? <Text textAlign="center" fontSize="14px">{format(new Date(val), 'MM/dd/yyyy')}</Text>
            : <Text color="gray.400" fontSize="14px">N/A</Text>;
        }
        return <AttachmentCell attachments={attachmentNames} />;
      },
    }),

    columnHelper.accessor('completed_date', {
      id: 'completed_date',
      header: () => 'Completed Date',
      subHeader: {
        1: { label: 'Completed Date', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const row             = info.row.original;
        const isBatch         = info.row.depth === 0;
        const effectiveStatus = isBatch ? getBatchStatus(row.subRows) : row.status;
        return <CompletedDateCell completedDate={info.getValue()} status={effectiveStatus} />;
      },
    }),

    columnHelper.accessor('status', {
      id: 'status',
      header: () => 'Status',
      subHeader: {
        1: { label: 'Status', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const isBatch = info.row.depth === 0;
        return isBatch
          ? <BatchStatusIndicator subRows={info.row.original.subRows} />
          : <ChartStatusBadge status={info.getValue()} />;
      },
    }),

    columnHelper.accessor(row => row, {
      id: 'charts_specialty',
      header: () => 'Files',
      subHeader: {
        1: { label: 'Specialty', ...CHART_SUBHEADER, sortable: true },
      },
      sortValue: row => row.specialty?.name ?? '',
      cell: info => {
        const row     = info.getValue();
        const isBatch = info.row.depth === 0;
        if (isBatch) {
          return row.charts != null
            ? <Text textAlign="center" fontSize="14px">{row.charts}</Text>
            : <Text color="gray.400" fontSize="14px">N/A</Text>;
        }
        if (!row.specialty)
          return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
        return row.specialty?.name
          ? <Text textAlign="center" fontSize="13px">{row.specialty.name}</Text>
          : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
      },
    }),

    columnHelper.accessor(row => row, {
      id: 'providers',
      header: () => 'Providers',
      enableSorting: false,
      subHeader: {
        1: { label: 'Providers', ...CHART_SUBHEADER },
      },
      cell: info => {
        const rowVal = info.getValue();
        const isBatch = info.row.depth === 0;
        if (isBatch) {
          return <AttachmentCell attachments={rowVal.batch_providers} />
        }
        return <AttachmentCell attachments={(rowVal.providers ?? []).map(chart_prov => chart_prov.name)} />
      }
    }),

    columnHelper.accessor('cq_score', {
      id: 'cq_score',
      header: () => 'RI Score',
      subHeader: {
        1: { label: 'RI Score', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        if (val == 0) return <Text color="gray.400" fontSize="14px">0</Text>;
        return val != null
          ? <Text textAlign="center" fontSize="14px">{val}</Text>
          : <Text color="gray.400" fontSize="14px">N/A</Text>;
      },
    }),

    columnHelper.accessor(row => row.upload_id, {
      id: 'action',
      header: () => '',
      enableSorting: false,
      cell: info => {
        const cellValue = info.getValue();
        const isBatch = info.row.depth === 0;
        const status = info.row.original.status;
        const isActionable = status === 'completed' || status === 'rebuttal';
        const batchId = info.getValue();

        return (
          <HStack justifyContent="start" spacing={2}>
            <Tooltip
              label={!isActionable ? "Available once reviewed." : ""}
              isDisabled={isActionable}
              placement="top"
              hasArrow
            >
              <Box>
                <PrimaryButton
                  bg={AppColors.secondary}
                  fontSize="14px"
                  fontWeight="normal"
                  size="sm"
                  borderRadius="3xl"
                  px={5}
                  isDisabled={!isActionable}
                  _hover={{
                    transition: 'all .1s ease',
                    bgColor: AppColors.white,
                    color: AppColors.secondary,
                    outline: `2px solid ${AppColors.secondary}`
                  }}
                  onClick={() => {
                    if (!isActionable) return;

                    if (isBatch) {
                      getRebuttedCharts.saveNavigationState(
                        PAGE_NAME,
                        TABLE_NAME,
                        info.row.original.upload_id
                      );
                      navigate(
                        ROUTES.VIEW_BATCH_AUDIT_SHEET.replace(':batchId', cellValue)
                      );
                    } else {
                      let routeFormat = { uploadId: info.row.original.id };
                      let route = ROUTES.VIEW_AUDIT_SHEET;
                      navigate(replaceRoute(route, routeFormat));
                    }
                  }}
                >
                  {isBatch ? 'View Batch' : 'View File'}
                </PrimaryButton>
              </Box>
            </Tooltip>

            {!isBatch ? (
              <AuditSheetFileViewer
                auditData={info.row.original}
                icon={<EyeAvatarIcon style={{ cursor: 'pointer' }} />}
              />
            ) : (
              <NotesDrawer
                tabType={"REBUTTAL"}
                batchId={batchId}
                onOpenNotes={obj => onOpenNotes(obj, batchId, "REBUTTAL")}
                style={{
                  width: 35,
                  height: 35,
                  cursor: 'pointer',
                  display: 'inline-block',
                  transition: "0.3s",
                  filter: "invert(1)"
                }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              />
            )}
          </HStack>
        );
      }
    }),

  ], [isRebuttedChartLoading]);

  function handlePreviousPage() {
    getRebuttedCharts.loadPrevious(PAGE_NAME, TABLE_NAME);
  }

  function handleNextPage() {
    getRebuttedCharts.loadNext(PAGE_NAME, TABLE_NAME);
  }

  function handleSizeChange(size) {
    getRebuttedCharts.loadSize(PAGE_NAME, TABLE_NAME, size);
  }

  function handlePageChangeByNumber(pageNumber) {
    getRebuttedCharts.loadPageByNumber(PAGE_NAME, TABLE_NAME, pageNumber);
  }

  return (
    <Box>
      <CQTreeTable
        ref={tableRef}
        data={rebuttedCharts}
        columns={columns}
        getSubRows={(row) => row.subRows}
        manualPagination={false}
        manualSortBy={false}
        isLoading={isRebuttedChartLoading}
        {...createTreeTableProps(highlightedRowId)}
        {...paginationProps(getRebuttedCharts)}
        onAsc={header => {
          setOrdering(header);
        }}
        onDesc={header => {
          setOrdering(`-${header}`);
        }}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onSizeChange={handleSizeChange}
        onPageChangeByNumber={handlePageChangeByNumber}
      />
    </Box>
  );
};

export default RebuttedCharts;
